import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import sysJwt from "../../../utils/sysJwt";
import { post } from "../../../utils/sysAxios";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import TicketDetailModal from "./TicketDetailModal";
import CreateTicketModal from "./CreateTicketModal";
import { ExportSheet } from "react-xlsx-sheet";
import XLSX from "xlsx";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

.tk-root {
  font-family: 'DM Sans', sans-serif;
  background: #F0F2F5;
  min-height: 100vh;
  color: #1A1D23;
  margin: -12px -12px -12px -2px;
  width: calc(100% + 14px);
}
[dir="rtl"] .tk-root,
[dir="rtl"] .tk-root *:not(i):not([class*="fa"]):not([class*="pi"]) {
  font-family: 'Cairo', 'DM Sans', sans-serif !important;
}

/* RTL table alignment */
[dir="rtl"] .tk-table th { text-align: right !important; }
[dir="rtl"] .tk-table td { text-align: right !important; }
[dir="rtl"] .tk-table th .sort-icon { margin-left: 0; margin-right: 4px; }

/* RTL search bar */
[dir="rtl"] .tk-search-wrap i { left: auto !important; right: 11px !important; }
[dir="rtl"] .tk-search-wrap input { padding: 8px 32px 8px 12px !important; }

/* ── Header ── */
.tk-header {
  background: #1A1D23;
  padding: 18px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  border-bottom: 3px solid #3B82F6;
}
.tk-header-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.02em;
}
.tk-header-title .accent { color: #60A5FA; }
.tk-header-count {
  background: #3B82F6;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  font-family: 'DM Mono', monospace;
}

/* ── Filter bar ── */
.tk-filters {
  background: #fff;
  border-bottom: 1px solid #E5E9EF;
  padding: 12px 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
}
.tk-search-wrap {
  position: relative;
  flex: 1;
  min-width: 150px;
  max-width: 260px;
}
.tk-search-wrap i {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  font-size: 0.85rem;
  z-index: 1;
}
.tk-search-wrap input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1.5px solid #E5E9EF;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  transition: border-color .15s;
  color: #1A1D23;
}
.tk-search-wrap input:focus { border-color: #3B82F6; }
.tk-filters .p-dropdown {
  border-radius: 8px !important;
  font-size: 0.83rem !important;
  width: 130px !important;
  min-width: 110px !important;
  flex-shrink: 0 !important;
  border: 1.5px solid #E5E9EF !important;
}
.tk-filters .p-calendar {
  width: 190px !important;
  flex-shrink: 0 !important;
}
.tk-filters .p-calendar input {
  font-size: 0.83rem !important;
  border-radius: 8px !important;
  width: 100% !important;
}
.tk-btn-search {
  background: #3B82F6 !important;
  border: none !important;
  border-radius: 8px !important;
  font-size: 0.83rem !important;
  padding: 8px 18px !important;
  font-family: 'DM Sans', sans-serif !important;
  flex-shrink: 0 !important;
}
.tk-btn-search:hover { background: #2563EB !important; }

/* ── Stats strip ── */
.tk-stats {
  display: flex;
  gap: 0;
  background: #fff;
  border-bottom: 1px solid #E5E9EF;
  overflow-x: auto;
}
.tk-stat {
  padding: 10px 22px;
  border-right: 1px solid #E5E9EF;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background .15s;
  white-space: nowrap;
  position: relative;
}
.tk-stat:hover { background: #F8FAFC; }
.tk-stat.active { background: #EFF6FF; }
.tk-stat.active::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 2px;
  background: #3B82F6;
}
.tk-stat-num {
  font-size: 1.1rem;
  font-weight: 700;
  font-family: 'DM Mono', monospace;
  color: #1A1D23;
}
.tk-stat-num.red { color: #DC2626; }
.tk-stat-num.amber { color: #D97706; }
.tk-stat-num.green { color: #16A34A; }
.tk-stat-label {
  font-size: 0.75rem;
  color: #6B7280;
  font-weight: 500;
}

/* ── Table wrapper ── */
.tk-body { padding: 0 0 24px; }
.tk-table-card {
  background: #fff;
  border-radius: 0;
  border-top: 1px solid #E5E9EF;
  border-bottom: 1px solid #E5E9EF;
  overflow: hidden;
  overflow-x: auto;
}
.tk-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  table-layout: fixed;
  min-width: 900px;
}
.tk-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E5E9EF;
}
.tk-table th {
  padding: 11px 14px;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #9CA3AF;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.tk-table th:hover { color: #3B82F6; }
.tk-table th .sort-icon { margin-left: 4px; opacity: .5; }
.tk-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #F3F4F6;
  color: #374151;
  vertical-align: middle;
}
.tk-table tbody tr { transition: background .12s; }
.tk-table tbody tr:hover td { background: #F8FAFC; }
.tk-table tbody tr:last-child td { border-bottom: none; }

/* ID cell */
.tk-id {
  font-family: 'DM Mono', monospace;
  font-size: 0.75rem;
  color: #9CA3AF;
}
/* Title cell */
.tk-title-cell {
  font-weight: 500;
  color: #1A1D23;
  max-width: 220px;
  min-width: 0;
  width: 220px;
}
.tk-title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
}
.tk-subtitle {
  font-size: 0.72rem;
  color: #9CA3AF;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
}

/* Status badge */
.tk-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.tk-status-dot { width: 6px; height: 6px; border-radius: 50%; }
.tk-status.open    { background: #EFF6FF; color: #1D4ED8; }
.tk-status.open .tk-status-dot { background: #3B82F6; }
.tk-status.inprog  { background: #FFFBEB; color: #B45309; }
.tk-status.inprog .tk-status-dot { background: #F59E0B; }
.tk-status.done    { background: #F0FDF4; color: #15803D; }
.tk-status.done .tk-status-dot { background: #22C55E; }
.tk-status.closed  { background: #F3F4F6; color: #6B7280; }
.tk-status.closed .tk-status-dot { background: #9CA3AF; }

/* SLA pill */
.tk-sla {
  font-size: 0.7rem;
  font-family: 'DM Mono', monospace;
  padding: 2px 7px;
  border-radius: 4px;
  white-space: nowrap;
}
.tk-sla.ok     { background: #F0FDF4; color: #15803D; }
.tk-sla.warn   { background: #FFFBEB; color: #B45309; }
.tk-sla.breach { background: #FEF2F2; color: #DC2626; }

/* Actions */
.tk-actions { display: flex; gap: 4px; }
.tk-action-btn {
  width: 30px; height: 30px;
  border-radius: 7px;
  border: 1.5px solid #E5E9EF;
  background: #fff;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.82rem;
  color: #6B7280;
  transition: all .15s;
}
.tk-action-btn:hover { border-color: #3B82F6; color: #3B82F6; background: #EFF6FF; }
.tk-action-btn.danger:hover { border-color: #DC2626; color: #DC2626; background: #FEF2F2; }
.tk-action-btn:disabled { opacity: .4; cursor: not-allowed; }

/* Unassigned chip */
.tk-unassigned {
  font-size: 0.72rem;
  color: #DC2626;
  background: #FEF2F2;
  padding: 2px 8px;
  border-radius: 4px;
}

/* Pagination */
.tk-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #F3F4F6;
  font-size: 0.8rem;
  color: #6B7280;
  flex-wrap: wrap;
  gap: 8px;
}
.tk-page-btns { display: flex; gap: 4px; }
.tk-page-btn {
  width: 30px; height: 30px;
  border-radius: 6px;
  border: 1.5px solid #E5E9EF;
  background: #fff;
  cursor: pointer;
  font-size: 0.8rem;
  color: #374151;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.tk-page-btn:hover { border-color: #3B82F6; color: #3B82F6; }
.tk-page-btn.active { background: #3B82F6; border-color: #3B82F6; color: #fff; }
.tk-page-btn:disabled { opacity: .4; cursor: not-allowed; }

/* Empty state */
.tk-empty {
  text-align: center;
  padding: 60px 20px;
  color: #9CA3AF;
}
.tk-empty i { font-size: 2.5rem; margin-bottom: 12px; display: block; opacity: .4; }
.tk-empty p { font-size: 0.9rem; margin: 0; }

/* Loading shimmer */
.tk-shimmer {
  background: linear-gradient(90deg, #F3F4F6 25%, #E5E9EF 50%, #F3F4F6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  border-radius: 4px;
  height: 14px;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

const PAGE_SIZE = 15;

const STATUS_COLORS = {
  "Submitted":           "#6C757D",
  "Assigned to Team":    "#FFB200",
  "Employee in the way": "#FFB200",
  "Processing":          "#F57F17",
  "Completed":           "#28A745",
  // fallbacks
  "Open":        "#6C757D",
  "In Progress": "#F57F17",
  "Resolved":    "#28A745",
  "Closed":      "#28A745",
};

function getStatusColor(name) {
  return STATUS_COLORS[name] || "#6C757D";
}

function getSlaClass(elapsedMinutes, slaMinutes) {
  if (!slaMinutes) return null;
  const pct = (elapsedMinutes / slaMinutes) * 100;
  if (pct >= 100) return "breach";
  if (pct >= 80)  return "warn";
  return "ok";
}

function fmtElapsed(minutes) {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

class Tickets extends Component {
  state = {
    // Data
    xlsxHeaders: [],
    tableData: [],
    filteredData: [],
    pagedData: [],

    // Filter dropdowns
    statusOptions: [],
    typeOptions: [],
    compoundOptions: [],
    employeeOptions: [],

    // Filter values
    searchText: "",
    statusFilter: null,
    typeFilter: null,
    compoundFilter: null,
    employeeFilter: null,
    dates: [
      new Date(new Date().setDate(new Date().getDate() - 60)),
      new Date(),
    ],
    activeStatTab: "all",   // all | open | unassigned | breached

    // Sorting
    sortField: "INSERT_DATE",
    sortDir: "desc",

    // Pagination
    page: 1,

    // Modal
    modalOpen: false,
    selectedTicketId: null,
    selectedTicketData: null,
    createModalOpen: false,

    loading: false,
  };

  componentDidMount() {
    this.loadDropdowns();
    this.getData();
  }

  // ── API calls ──────────────────────────────────────────────────────────────

  loadDropdowns() {
    post("/api/data/GetTicketDashboardFilters", {}, () => {}).then((res) => {
      const d = res.data.respData.data;
      this.setState({
        statusOptions:   d.statuses   || [],
        typeOptions:     d.ticketTypes|| [],
        compoundOptions: d.compounds  || [],
        employeeOptions: d.employees  || [],
      });
    });
    // Also load statuses separately if your filter endpoint doesn't include them
    post("/api/data/GetTicketSatuses", null, () => {}).then((res) => {
      const list = res.data.respData.data || [];
      this.setState({ statusOptions: list.map(s => ({ label: s.STATUS_EN, value: s.ID })) });
    }).catch(() => {});
  }

  getData() {
    this.setState({ loading: true });
    post(
      "/api/data/GetTickets",
      { dateFrom: this.state.dates[0], dateTo: this.state.dates[1] },
      () => {}
    ).then((res) => {
      const tableData = res.data.respData.data || [];
      this.setState({ tableData, loading: false }, this.applyFilters);
      this.buildExportHeaders(tableData);
    }).catch(() => this.setState({ loading: false }));
  }

  buildExportHeaders(rows) {
    if (!rows || rows.length === 0) return;
    let xlsxHeaders = [];
    for (var property in rows[0]) {
      if (rows[0].hasOwnProperty(property)) {
        xlsxHeaders.push({ title: property, dataIndex: property });
      }
    }
    this.setState({ xlsxHeaders });
  }

  // ── Client-side filter + sort + page ──────────────────────────────────────

  applyFilters = () => {
    const {
      tableData, searchText, statusFilter, typeFilter,
      compoundFilter, employeeFilter, activeStatTab,
      sortField, sortDir, page,
    } = this.state;

    let data = [...tableData];

    // stat tab quick filter
    if (activeStatTab === "open")       data = data.filter(r => r.TICKET_STATUS_ID === 1 || r.STATUS_EN === "Open");
    if (activeStatTab === "unassigned") data = data.filter(r => !r.EMPLOYEE_ID && !r.EMPLOYEE_NAME);
    if (activeStatTab === "breached")   data = data.filter(r => r.SLA_BREACHED);

    // text search across key fields
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(r =>
        (r.TITLE        || "").toLowerCase().includes(q) ||
        (r.BODY         || "").toLowerCase().includes(q) ||
        (r.OWNER_NAME   || "").toLowerCase().includes(q) ||
        (r.UNIT_NAME    || "").toLowerCase().includes(q) ||
        (r.TYPE_NAME    || "").toLowerCase().includes(q) ||
        (String(r.ID)   || "").includes(q)
      );
    }

    if (statusFilter)   data = data.filter(r => r.TICKET_STATUS_ID === statusFilter);
    if (typeFilter)     data = data.filter(r => r.TICKET_TYPE_ID   === typeFilter);
    if (compoundFilter) data = data.filter(r => r.COMPOUND_ID      === compoundFilter || r.COMPOUND_NAME === compoundFilter);
    if (employeeFilter) data = data.filter(r => r.EMPLOYEE_ID      === employeeFilter);

    // sort
    data.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") {
  av = av.toLowerCase();
  bv = bv.toLowerCase();
}
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
    const safePage   = Math.min(page, totalPages);
    const pagedData  = data.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    this.setState({ filteredData: data, pagedData, page: safePage });
  };

  setSort(field) {
    this.setState(s => ({
      sortField: field,
      sortDir: s.sortField === field && s.sortDir === "asc" ? "desc" : "asc",
      page: 1,
    }), this.applyFilters);
  }

  setPage(p) { this.setState({ page: p }, this.applyFilters); }

  setFilter(key, val) {
    this.setState({ [key]: val, page: 1 }, this.applyFilters);
  }

  setStatTab(tab) {
    this.setState({ activeStatTab: tab, page: 1 }, this.applyFilters);
  }

  // ── Modal ──────────────────────────────────────────────────────────────────

  openModal(row) {
    this.setState({ modalOpen: true, selectedTicketId: row.ID, selectedTicketData: row });
  }

  closeModal(refresh = false) {
    this.setState({ modalOpen: false, selectedTicketId: null, selectedTicketData: null });
    if (refresh) this.getData();
  }

  // ── Derived stats for the strip ────────────────────────────────────────────

  getStats() {
    const { tableData } = this.state;
    const total      = tableData.length;
    const open       = tableData.filter(r => r.STATUS_EN === "Open").length;
    const unassigned = tableData.filter(r => !r.EMPLOYEE_NAME).length;
    const breached   = tableData.filter(r => r.SLA_BREACHED).length;
    return { total, open, unassigned, breached };
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  renderSortIcon(field) {
    const { sortField, sortDir } = this.state;
    if (sortField !== field) return <i className="fa fa-sort sort-icon" />;
    return <i className={`fa fa-sort-${sortDir === "asc" ? "asc" : "desc"} sort-icon`} style={{ opacity: 1, color: "#3B82F6" }} />;
  }

  renderShimmer() {
    return Array.from({ length: 8 }).map((_, i) => (
      <tr key={i}>
        {[60, 140, 110, 100, 120, 80, 90, 80].map((w, j) => (
          <td key={j}><div className="tk-shimmer" style={{ width: w }} /></td>
        ))}
      </tr>
    ));
  }

  render() {
    const { t } = this.props;
    const {
      pagedData, filteredData, loading,
      searchText, statusFilter, typeFilter, compoundFilter, employeeFilter,
      dates, activeStatTab, page,
      statusOptions, typeOptions, compoundOptions, employeeOptions,
      modalOpen, selectedTicketId, selectedTicketData,
      createModalOpen,
    } = this.state;

    const stats      = this.getStats();
    const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));

    const toOpts = (arr, labelKey = "label", valueKey = "value") =>
      [{ label: t("filter.all"), value: null }, ...arr.map(x => ({ label: x[labelKey] || x.label, value: x[valueKey] || x.value }))];

    return (
      <div className="tk-root fade-in">
        <style>{styles}</style>

        {/* ── Header ── */}
        <div className="tk-header">
          <div className="tk-header-title">
            <i className="fa fa-ticket" />
            {t("pages.ticketManagement")}
            <span className="tk-header-count">{stats.total}</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {sysJwt.isAuthorized(["CB8E"]) && (
              <Button
                icon="fa fa-plus"
                label={t("tick.createTicket")}
                className="p-button-rounded p-button-sm"
                style={{ background: "#16A34A", border: "none", fontSize: "0.82rem" }}
                onClick={() => this.setState({ createModalOpen: true })}
              />
            )}
            <Button
              icon="fa fa-refresh"
              label={t("btn.refresh")}
              className="p-button-rounded p-button-sm"
              style={{ background: "#3B82F6", border: "none", fontSize: "0.82rem" }}
              disabled={loading}
              onClick={() => this.getData()}
            />
            {filteredData.length > 0 && (
              <ExportSheet
                header={this.state.xlsxHeaders}
                dataSource={filteredData}
                fileName="TicketsExport"
                isRequiredNameDate={false}
                xlsx={XLSX}
              >
                <Button
                  icon="fa fa-file-excel-o"
                  label={t("btn.exportExcel")}
                  className="p-button-rounded p-button-sm"
                  style={{ background: "#16A34A", border: "none", fontSize: "0.82rem" }}
                />
              </ExportSheet>
            )}
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="tk-filters">
          {/* Search */}
          <div className="tk-search-wrap">
            <i className="fa fa-search" />
            <input
              type="text"
              placeholder={t("btn.search") + "…"}
              value={searchText}
              onChange={e => this.setFilter("searchText", e.target.value)}
            />
          </div>

          {/* Date range */}
          <Calendar
            selectionMode="range"
            showIcon
            value={dates}
            onChange={e => this.setState({ dates: e.value })}
            style={{ width: 190, flexShrink: 0 }}
            placeholder={t("filter.dateRange")}
          />
          <Button
            label={t("btn.search")}
            className="p-button-rounded tk-btn-search"
            disabled={loading}
            onClick={() => this.getData()}
          />

          <div style={{ width: 1, height: 24, background: "#E5E9EF" }} />

          {/* Dropdowns */}
          <Dropdown
            value={statusFilter}
            options={toOpts(statusOptions, "STATUS_EN", "ID")}
            onChange={e => this.setFilter("statusFilter", e.value)}
            placeholder={t("filter.status")}
          />
          <Dropdown
            value={typeFilter}
            options={toOpts(typeOptions, "TYPE_NAME_EN", "ID")}
            onChange={e => this.setFilter("typeFilter", e.value)}
            placeholder={t("filter.type")}
          />
          <Dropdown
            value={compoundFilter}
            options={toOpts(compoundOptions)}
            onChange={e => this.setFilter("compoundFilter", e.value)}
            placeholder={t("filter.compound")}
          />
          <Dropdown
            value={employeeFilter}
            options={toOpts(employeeOptions)}
            onChange={e => this.setFilter("employeeFilter", e.value)}
            placeholder={t("filter.employee")}
          />
        </div>

        {/* ── Stats strip ── */}
        <div className="tk-stats">
          {[
            { key: "all",        label: t("tick.allTickets"),  num: stats.total,      cls: "" },
            { key: "open",       label: t("tick.open"),        num: stats.open,       cls: "blue" },
            { key: "unassigned", label: t("tick.unassigned"),  num: stats.unassigned, cls: "amber" },
            { key: "breached",   label: t("tick.slaBreached"), num: stats.breached,   cls: "red" },
          ].map(s => (
            <div
              key={s.key}
              className={`tk-stat ${activeStatTab === s.key ? "active" : ""}`}
              onClick={() => this.setStatTab(s.key)}
            >
              <span className={`tk-stat-num ${s.cls}`}>{s.num}</span>
              <span className="tk-stat-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Table ── */}
        <div className="tk-body">
          <div className="tk-table-card">
            <table className="tk-table">
              <thead>
                <tr>
                  <th onClick={() => this.setSort("ID")}>{t("col.id")} {this.renderSortIcon("ID")}</th>
                  <th onClick={() => this.setSort("OWNER_NAME")}>{t("col.owner")} {this.renderSortIcon("OWNER_NAME")}</th>
                  <th onClick={() => this.setSort("TITLE")}>{t("col.title")} {this.renderSortIcon("TITLE")}</th>
                  <th onClick={() => this.setSort("TYPE_NAME")}>{t("col.type")} {this.renderSortIcon("TYPE_NAME")}</th>
                  <th onClick={() => this.setSort("COMPOUND_NAME")}>{t("col.compound")} {this.renderSortIcon("COMPOUND_NAME")}</th>
                  <th onClick={() => this.setSort("STATUS_EN")}>{t("col.status")} {this.renderSortIcon("STATUS_EN")}</th>
                  <th>{t("col.employee")}</th>
                  <th onClick={() => this.setSort("INSERT_DATE")}>{t("col.created")} {this.renderSortIcon("INSERT_DATE")}</th>
                  <th>{t("col.sla")}</th>
                  <th>{t("col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? this.renderShimmer()
                  : pagedData.length === 0
                  ? (
                    <tr>
                      <td colSpan={10}>
                        <div className="tk-empty">
                          <i className="fa fa-inbox" />
                          <p>{t("tick.noTicketsFound")}</p>
                        </div>
                      </td>
                    </tr>
                  )
                  : pagedData.map((row, i) => {
                    const slaClass = getSlaClass(row.ELAPSED_MINUTES, row.SLA_MINUTES);
                    return (
                      <tr key={row.ID || i}>
                        <td><span className="tk-id">#{row.ID}</span></td>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: "0.84rem" }}>{row.OWNER_NAME}</div>
                          <div style={{ fontSize: "0.72rem", color: "#9CA3AF" }}>{row.UNIT_NAME}</div>
                        </td>
                        <td style={{ maxWidth: 220, width: 220, overflow: "hidden" }}>
                          <div className="tk-title-cell">
                            <span className="tk-title-text" title={row.TITLE}>{row.TITLE}</span>
                            <span className="tk-subtitle" title={row.BODY}>{row.BODY}</span>
                          </div>
                        </td>
                        <td style={{ fontSize: "0.8rem" }}>{row.TYPE_NAME}</td>
                        <td style={{ fontSize: "0.8rem" }}>{row.COMPOUND_NAME}</td>
                        <td>
                          {(() => {
                            const color = getStatusColor(row.STATUS_EN);
                            return (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "3px 10px",
                                borderRadius: 20,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                background: `${color}18`,
                                color: color,
                                border: `1px solid ${color}40`,
                                whiteSpace: "nowrap",
                              }}>
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                {row.STATUS_EN}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {row.EMPLOYEE_NAME
                            ? <span style={{ fontSize: "0.82rem" }}>{row.EMPLOYEE_NAME}</span>
                            : <span className="tk-unassigned">{t("tick.unassigned")}</span>
                          }
                        </td>
                        <td style={{ fontSize: "0.78rem", color: "#6B7280", fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
                          {row.INSERT_DATE ? new Date(row.INSERT_DATE).toLocaleDateString() : "—"}
                        </td>
                        <td>
                          {slaClass
                            ? <span className={`tk-sla ${slaClass}`}>{fmtElapsed(row.ELAPSED_MINUTES)} / {fmtElapsed(row.SLA_MINUTES)}</span>
                            : <span style={{ color: "#9CA3AF", fontSize: "0.75rem" }}>—</span>
                          }
                        </td>
                        <td>
                          <div className="tk-actions">
                            {sysJwt.isAuthorized(["CB3F"]) && (
                              <button
                                className="tk-action-btn"
                                title="View / Reply"
                                disabled={this.props.isLoading}
                                onClick={() => this.openModal(row)}
                              >
                                <i className="fa fa-comment" />
                              </button>
                            )}
                            {sysJwt.isAuthorized(["CB7C"]) && row.NEED_EMPLOYEE && (
                              <button
                                className="tk-action-btn"
                                title="Assign Employee"
                                disabled={this.props.isLoading}
                                onClick={() => this.openModal({ ...row, _defaultTab: "assign" })}
                              >
                                <i className="fa fa-user-plus" />
                              </button>
                            )}
                            {sysJwt.isAuthorized(["CB7D"]) && (
                              <button
                                className="tk-action-btn"
                                title="Change Status"
                                disabled={this.props.isLoading}
                                onClick={() => this.openModal({ ...row, _defaultTab: "status" })}
                              >
                                <i className="fa fa-exchange" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>

            {/* Pagination */}
            {!loading && filteredData.length > 0 && (
              <div className="tk-pagination">
                <span>
                  {t("tick.showingPagination", {
                    from: ((page - 1) * PAGE_SIZE) + 1,
                    to:   Math.min(page * PAGE_SIZE, filteredData.length),
                    total: filteredData.length,
                  })}
                </span>
                <div className="tk-page-btns">
                  <button
                    className="tk-page-btn"
                    disabled={page === 1}
                    onClick={() => this.setPage(page - 1)}
                  >
                    <i className="fa fa-chevron-left" />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let p = i + 1;
                    if (totalPages > 7) {
                      if (page <= 4) p = i + 1;
                      else if (page >= totalPages - 3) p = totalPages - 6 + i;
                      else p = page - 3 + i;
                    }
                    return (
                      <button
                        key={p}
                        className={`tk-page-btn ${p === page ? "active" : ""}`}
                        onClick={() => this.setPage(p)}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    className="tk-page-btn"
                    disabled={page === totalPages}
                    onClick={() => this.setPage(page + 1)}
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Detail Modal ── */}
        {modalOpen && (
          <TicketDetailModal
            ticketId={selectedTicketId}
            ticketData={selectedTicketData}
            defaultTab={selectedTicketData?._defaultTab || "replies"}
            isLoading={this.props.isLoading}
            updateIsLoading={this.props.updateIsLoading}
            onClose={(refresh) => this.closeModal(refresh)}
          />
        )}

        {/* ── Create Ticket Modal ── */}
        {createModalOpen && (
          <CreateTicketModal
            typeOptions={typeOptions}
            onClose={(refresh) => {
              this.setState({ createModalOpen: false });
              if (refresh) this.getData();
            }}
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(Tickets);
